import { createClient } from '@supabase/supabase-js';

// Helper function to get environment variable from secrets or process.env
const getEnvVar = (varName: string): string | undefined => {
  // First try to get from window.REACT_APP_SECRETS (injected by server)
  if (typeof window !== 'undefined' && (window as any).REACT_APP_SECRETS?.[varName]) {
    return (window as any).REACT_APP_SECRETS[varName];
  }

  // Fallback to process.env
  return process.env[varName];
};

// Expect CRA-style env vars
const supabaseUrl = getEnvVar('REACT_APP_SUPABASE_URL');
const supabaseAnonKey = getEnvVar('REACT_APP_SUPABASE_ANON_KEY');

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Validate Supabase configuration

if (!supabaseUrl || !supabaseAnonKey) {
  if (isDevelopment) {
    // In development, show a clear warning
    console.warn('Supabase env vars are missing. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
  } else {
    // In production, log error but don't crash the app
    console.error('Supabase configuration is missing. Some features may not work properly.');
  }
}

// Create a mock client if environment variables are missing
let supabase: any;

if (!supabaseUrl || !supabaseAnonKey) {
  // Create a mock client that doesn't crash the app
  supabase = {
    auth: {
      signIn: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
    },
    rpc: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    from: () => ({
      select: () => ({
        eq: () => ({
          single: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
          order: () => ({
            limit: () => Promise.resolve({ data: [], error: { message: 'Supabase not configured' } })
          }),
          then: (resolve: any) => resolve({ data: [], error: { message: 'Supabase not configured' } })
        }),
        order: () => ({
          limit: () => Promise.resolve({ data: [], error: { message: 'Supabase not configured' } })
        }),
        then: (resolve: any) => resolve({ data: [], error: { message: 'Supabase not configured' } })
      }),
      insert: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      update: () => ({
        eq: () => ({
          select: () => ({
            single: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } })
          })
        })
      }),
      delete: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    }),
  };
} else {
  // Create the real Supabase client with proper configuration
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    global: {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    }
  });
}

export { supabase };
export default supabase;
